import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { LiveZone } from '../lib/fleet-live-layout';
import { FleetLiveIcon } from './FleetLiveIcon';
import { FleetLivePlaceMenu } from './FleetLivePlaceMenu';
import { FleetLivePowerIcon } from './FleetLivePowerIcon';

// A Fleet Live panel's header controls (#6779, prototype `ctl`, `:3455-3465`): the grip
// ("Move <panel>. Drag it to Top, Middle, Side or Bottom", pointer-only), "Place <panel>"
// (`aria-expanded`, opening the "Place in" menu, the keyboard path) and #6778's "Hide
// <panel>". Viewer preferences, shown to every role.

export type FleetLivePanelControlsProps = {
  /** The panel's name, already translated. */
  readonly panel: string;
  readonly zone: LiveZone;
  readonly menuOpen: boolean;
  readonly onGrip: (e: PointerEvent) => void;
  readonly onToggleMenu: () => void;
  readonly onPlace: (zone: LiveZone) => void;
  readonly onReset: () => void;
  readonly onHide: () => void;
  /** The "Place" button, so the section can return focus to it after a move. */
  readonly placeRef?: (el: HTMLButtonElement) => void;
};

export function FleetLivePanelControls(props: FleetLivePanelControlsProps): JSX.Element {
  let root: HTMLDivElement | undefined;
  let place: HTMLButtonElement | undefined;
  return (
    <div class="fleet-live__ctl" ref={root}>
      <button
        type="button"
        class="fleet-live__btn fleet-live__grip"
        aria-label={t('fleet.live.move_panel', { panel: props.panel })}
        title={t('fleet.live.drag_to_move')}
        onPointerDown={(e) => props.onGrip(e)}
      >
        <FleetLiveIcon name="move" />
      </button>
      <button
        type="button"
        class="fleet-live__btn"
        aria-label={t('fleet.live.place_panel', { panel: props.panel })}
        title={t('fleet.live.place')}
        aria-expanded={props.menuOpen}
        ref={(el) => {
          place = el;
          props.placeRef?.(el);
        }}
        onClick={() => props.onToggleMenu()}
      >
        <FleetLiveIcon name="layout" />
      </button>
      <button
        type="button"
        class="fleet-live__hide"
        aria-label={t('fleet.live.hide_panel', { panel: props.panel })}
        title={t('fleet.live.hide')}
        onClick={() => props.onHide()}
      >
        <FleetLivePowerIcon />
      </button>
      <Show when={props.menuOpen}>
        <FleetLivePlaceMenu
          panel={props.panel}
          current={props.zone}
          within={() => root}
          onPlace={(zone) => props.onPlace(zone)}
          onReset={() => props.onReset()}
          onClose={(refocus) => {
            if (props.menuOpen) props.onToggleMenu();
            if (refocus) place?.focus();
          }}
        />
      </Show>
    </div>
  );
}
